import { InjectionToken } from "@angular/core";
import { Invoice, MicroBilling, Organization } from "../../../pojo/Pricing";
import { OrgInvoiceRecords } from "./org-invoice-records";

/** Shared down the tab so the compose panel's scoped store can be seeded from what it holds. */
export const ORG_INVOICE_CACHE = new InjectionToken<OrgInvoiceCache>("OrgInvoiceCache");

/**
 * The invoices this tab has already read, filed under the event they bill for.
 *
 * The date ranges nest - a month sits inside the quarter, which sits inside the year - so without
 * this, every switch between them reads invoices the tab was already holding. It lives as long as
 * the tab, and is emptied by the refresh button.
 */
export class OrgInvoiceCache {
    private readonly byEventId = new Map<number, Invoice[]>();

    /**
     * The organizations named across every read, which is what puts a name to an org charged on an
     * invoice. Accumulated rather than kept per read: an invoice seeded out of here may have come
     * from one batch while the org charged on it was named by another.
     */
    private readonly organizationById = new Map<number, Organization>();

    /** Read-level metadata, carried so a rebuilt envelope is a whole `MicroBilling`. Nothing reads it. */
    private envelope: ReadMetadata = EMPTY_METADATA;

    unread(eventIds: number[]): number[] {
        return eventIds.filter((eventId) => !this.byEventId.has(eventId));
    }

    read(eventIds: number[]): Invoice[] {
        const byInvoiceId = new Map<number, Invoice>();
        for (const eventId of eventIds) {
            for (const invoice of this.byEventId.get(eventId) ?? []) byInvoiceId.set(invoice.id, invoice);
        }
        return [...byInvoiceId.values()];
    }

    record(eventIds: number[], billing: MicroBilling | undefined): void {
        if (!eventIds.length) return;

        for (const organization of billing?.expandedInfo?.organizations ?? []) {
            this.organizationById.set(organization.organizationId, organization);
        }
        if (billing) {
            const { id, updated, sort, requestId } = billing;
            this.envelope = { id, updated, sort, requestId };
        }

        this.recordInvoices(eventIds, billing?.data?.items ?? []);
    }

    recordInvoices(eventIds: number[], invoices: Invoice[]): void {
        if (!eventIds.length) return;

        const asked = new Set(eventIds);
        const filed = new Map<number, Invoice[]>(eventIds.map((eventId): [number, Invoice[]] => [eventId, []]));

        for (const invoice of invoices) {
            const eventId = OrgInvoiceRecords.eventIdOf(invoice);
            filed.get(asked.has(eventId) ? eventId : eventIds[0])!.push(invoice);
        }

        for (const [eventId, held] of filed) this.byEventId.set(eventId, held);
    }

    billingFor(eventIds: number[]): MicroBilling | undefined {
        if (!eventIds.length || this.unread(eventIds).length) return undefined;

        return {
            ...this.envelope,
            data: { items: this.read(eventIds) },
            expandedInfo: { organizations: [...this.organizationById.values()] },
        };
    }

    clear(): void {
        this.byEventId.clear();
        this.organizationById.clear();
        this.envelope = EMPTY_METADATA;
    }
}

type ReadMetadata = Pick<MicroBilling, "id" | "updated" | "sort" | "requestId">;

const EMPTY_METADATA: ReadMetadata = { id: 0, updated: "", sort: [], requestId: 0 };
